import { useMemo, useState } from "react";
import { InvoiceListRowsSkeleton } from "../loading/SkeletonBlocks.jsx";
import { formatMoneyCents } from "../../lib/money.js";
import { formatBillingInstant } from "../../lib/stripeInstant.js";

function formatClientLabel(clientUserId) {
  const id = String(clientUserId ?? "").trim();
  if (!id) return "Unknown client";
  if (id.length <= 10) return id;
  return `Client …${id.slice(-6)}`;
}

function formatPurchaseAmount(purchase) {
  const amount = formatMoneyCents(purchase.amountCents, purchase.currency);
  if (purchase.billingType === "recurring") return `${amount} / mo`;
  return amount;
}

function formatPurchaseStatus(purchase) {
  if (purchase.cancelAtPeriodEnd) return "Cancels";
  if (purchase.accessGranted) {
    if (purchase.billingType === "recurring") return "Active";
    return "Paid";
  }
  return String(purchase.status ?? "Inactive");
}

function statusVariant(purchase) {
  if (purchase.cancelAtPeriodEnd) {
    return "border-hc-warning/35 bg-hc-warning-muted text-hc-warning";
  }
  if (purchase.accessGranted) {
    return "border-hc-success/35 bg-hc-success-muted text-hc-success";
  }
  return "border-hc-surface-border text-hc-text-dim";
}

function formatRenewal(purchase) {
  const end = purchase.currentPeriodEnd ?? purchase.termEnd;
  return formatBillingInstant(end) || "—";
}

function planLabel(purchase, planNameById) {
  const planId = String(purchase.planId ?? "").trim();
  if (planId && planNameById[planId]) return planNameById[planId];
  return purchase.billingType === "recurring" ? "Subscription" : "One-time package";
}

export default function ClientPurchasesList({
  purchases,
  planNameById = {},
  isLoading,
  className = "",
}) {
  const [showAll, setShowAll] = useState(false);

  const visiblePurchases = useMemo(() => {
    const sorted = [...(purchases ?? [])].sort((a, b) => {
      const aActive = a.accessGranted ? 1 : 0;
      const bActive = b.accessGranted ? 1 : 0;
      return bActive - aActive;
    });
    if (showAll) return sorted;
    return sorted.filter((purchase) => purchase.accessGranted);
  }, [purchases, showAll]);

  const hasHidden = (purchases ?? []).some((purchase) => !purchase.accessGranted);

  return (
    <section
      className={`overflow-hidden rounded-2xl border border-hc-surface-border bg-hc-surface ${className}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-hc-surface-border px-5 py-3.5">
        <div>
          <h2 className="text-sm font-semibold tracking-[-0.15px] text-hc-text">
            Client purchases
          </h2>
          <p className="mt-0.5 text-xs text-hc-text-dim">
            Billing from assigned clients on your payout account.
          </p>
        </div>
        {hasHidden ? (
          <button
            type="button"
            onClick={() => setShowAll((value) => !value)}
            className="text-xs font-medium text-hc-text-muted transition hover:text-hc-text"
          >
            {showAll ? "Show active only" : "Show all"}
          </button>
        ) : null}
      </div>

      {isLoading ? (
        <InvoiceListRowsSkeleton rows={3} />
      ) : visiblePurchases.length === 0 ? (
        <p className="px-5 py-6 text-xs text-hc-text-dim">
          {showAll ? "No client purchases yet." : "No active client billing yet."}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-hc-surface-border/80 text-xs text-hc-text-dim">
              <tr>
                <th className="px-5 py-3 font-medium">Client</th>
                <th className="px-5 py-3 font-medium">Plan</th>
                <th className="px-5 py-3 font-medium">Amount</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Renews / expires</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hc-surface-border/80">
              {visiblePurchases.map((purchase) => (
                <tr key={purchase.id}>
                  <td className="px-5 py-4 text-hc-text">
                    {formatClientLabel(purchase.clientUserId)}
                  </td>
                  <td className="px-5 py-4 text-hc-text-muted">
                    {planLabel(purchase, planNameById)}
                  </td>
                  <td className="px-5 py-4 font-medium text-hc-text">
                    {formatPurchaseAmount(purchase)}
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${statusVariant(purchase)}`}
                    >
                      {formatPurchaseStatus(purchase)}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-hc-text-dim">{formatRenewal(purchase)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
